import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2, Layers, Copy, RotateCcw, MoreVertical, Cookie } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import ModuleConfigFields from "./ModuleConfigFields";
import InstancePreviewModal from "@/components/admin/InstancePreviewModal";
import SectionBackgroundControls from "./SectionBackgroundControls";
import CustomScriptControls from "./CustomScriptControls";
import MilestonesDashboardModule from "@/components/modules/MilestonesDashboardModule";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MODULE_TYPES, CONFIGURABLE_TYPES, getDefaultConfig, getModuleTypeLabel, resetStyleConfig } from "./moduleDefaults";

const API = getApiUrl();

export default function ModuleInstances() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [allPages, setAllPages] = useState({ modular: [], preset: [], custom: [] });

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  // Preview modal — opened by clicking the eye button, type badge, or name.
  const [previewInstance, setPreviewInstance] = useState(null);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("hero");
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [configFields, setConfigFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Auto-save state — "idle" | "dirty" | "saving" | "saved" | "error"
  const [autosaveState, setAutosaveState] = useState("idle");
  const autosaveTimer = useRef(null);
  const isInitialLoad = useRef(false);

  // Master Admin CRUD-on-behalf-of-agent — only relevant when editing a
  // "milestones" instance. Lets the admin pick any agent + property to
  // manage that property's milestones directly from this dialog.
  const [milestoneAgents, setMilestoneAgents] = useState([]);
  const [milestoneAgentsLoading, setMilestoneAgentsLoading] = useState(false);
  const [selectedMilestoneAgentId, setSelectedMilestoneAgentId] = useState("");

  useEffect(() => {
    fetchInstances();
    fetchPages();
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/module-instances`, { withCredentials: true });
      const sorted = [...(r.data.instances || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      setInstances(sorted);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load module instances");
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const [modularRes, presetRes, customRes] = await Promise.all([
        axios.get(`${API}/admin/page-compositions`, { withCredentials: true }),
        axios.get(`${API}/navigation`, { withCredentials: true }),
        axios.get(`${API}/admin/custom-static-pages`, { withCredentials: true }),
      ]);
      setAllPages({
        modular: modularRes.data.compositions || [],
        preset: (presetRes.data || []).filter((p) => p.path && p.path !== "#"),
        custom: customRes.data.pages || [],
      });
    } catch (e) {
      console.error("Failed to fetch pages:", e);
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const r = await axios.post(`${API}/admin/upload?skip_watermark=true`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.url) {
        setConfigFields((prev) => ({ ...prev, [fieldName]: r.data.url }));
        toast.success("Image uploaded");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => {
    setNewName("");
    setNewType("hero");
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      setCreating(true);
      // Copy the parameters set on the module type in Module Library so a
      // fresh instance starts with the admin's saved defaults (theme,
      // scheduler, heading, etc.) rather than empty hard-coded values.
      let seed = {};
      try {
        const cfgRes = await axios.get(
          `${API}/module-config/${newType}`,
          { withCredentials: true }
        );
        seed = cfgRes.data?.config || {};
      } catch (_) { /* no saved defaults yet — fall back to hard-coded */ }
      const r = await axios.post(
        `${API}/admin/module-instances`,
        { name: newName.trim(), module_type: newType, config: getDefaultConfig(newType, seed) },
        { withCredentials: true }
      );
      toast.success("Instance created");
      setShowCreate(false);
      await fetchInstances();
      // Immediately open edit dialog for the new instance
      const created = r.data.instance;
      if (created) openEdit(created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create instance");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (inst) => {
    // Prevent the auto-save watcher from firing on initial hydration.
    isInitialLoad.current = true;
    setEditing(inst);
    setEditName(inst.name || "");
    setConfigFields(getDefaultConfig(inst.module_type, inst.config || {}));
    setAutosaveState("idle");
    // Clear the initial-load flag after the state updates settle.
    setTimeout(() => {
      isInitialLoad.current = false;
    }, 50);
    if (inst.module_type === "milestones") {
      setMilestoneAgentsLoading(true);
      axios
        .get(`${API}/admin/agents`, { withCredentials: true })
        .then((res) => {
          const agents = res.data || [];
          setMilestoneAgents(agents);
          setSelectedMilestoneAgentId(agents[0]?.agent_id || "");
        })
        .catch(() => setMilestoneAgents([]))
        .finally(() => setMilestoneAgentsLoading(false));
    }
  };

  // Debounced auto-save whenever editName or configFields change while the
  // edit dialog is open. Fires 800ms after the last change.
  useEffect(() => {
    if (!editing) return;
    if (isInitialLoad.current) return;
    setAutosaveState("dirty");
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      if (!editName.trim()) {
        setAutosaveState("error");
        return;
      }
      try {
        setAutosaveState("saving");
        await axios.put(
          `${API}/admin/module-instances/${editing.instance_id}`,
          { name: editName.trim(), config: configFields },
          { withCredentials: true }
        );
        setAutosaveState("saved");
      } catch (e) {
        setAutosaveState("error");
        toast.error(e.response?.data?.detail || "Auto-save failed");
      }
    }, 800);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [editName, configFields, editing?.instance_id]);

  // Flush any pending auto-save immediately (used before closing the dialog).
  const flushAutoSave = async () => {
    if (!editing) return;
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    if (autosaveState !== "dirty" && autosaveState !== "saving") return;
    if (!editName.trim()) return;
    try {
      setAutosaveState("saving");
      await axios.put(
        `${API}/admin/module-instances/${editing.instance_id}`,
        { name: editName.trim(), config: configFields },
        { withCredentials: true }
      );
      setAutosaveState("saved");
    } catch (e) {
      setAutosaveState("error");
      toast.error(e.response?.data?.detail || "Auto-save failed");
    }
  };

  const closeEditDialog = async () => {
    await flushAutoSave();
    setEditing(null);
    fetchInstances();
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }
    // Cancel any pending auto-save timer so we don't double-save.
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    try {
      setSaving(true);
      await axios.put(
        `${API}/admin/module-instances/${editing.instance_id}`,
        { name: editName.trim(), config: configFields },
        { withCredentials: true }
      );
      toast.success("Instance saved");
      setEditing(null);
      fetchInstances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save instance");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (inst) => {
    if (!confirm(`Delete instance "${inst.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/module-instances/${inst.instance_id}`, {
        withCredentials: true,
      });
      toast.success("Instance deleted");
      fetchInstances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete instance");
    }
  };

  // Quick enable/disable toggle from the row. Disabled instances are skipped
  // when rendering page overlays on the public site.
  const toggleEnabled = async (inst, checked) => {
    try {
      await axios.put(
        `${API}/admin/module-instances/${inst.instance_id}`,
        { enabled: checked },
        { withCredentials: true }
      );
      setInstances((prev) =>
        prev.map((i) => (i.instance_id === inst.instance_id ? { ...i, enabled: checked } : i))
      );
      toast.success(checked ? "Instance enabled" : "Instance disabled");
    } catch (e) {
      console.error(e);
      toast.error("Failed to toggle instance");
    }
  };

  const handleDuplicate = async (inst) => {
    try {
      const r = await axios.post(
        `${API}/admin/module-instances`,
        {
          name: `${inst.name} (Copy)`,
          module_type: inst.module_type,
          config: inst.config || {},
        },
        { withCredentials: true }
      );
      toast.success("Instance duplicated");
      fetchInstances();
      const created = r.data.instance;
      if (created) openEdit(created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to duplicate");
    }
  };

  // Wipes every per-instance style/colour override on this instance so it
  // falls straight back to the global Site Styles baseline. Content fields
  // (titles, links, text, images, toggles) are preserved.
  const handleResetStyles = async (inst) => {
    if (!confirm(
      `Reset all style and colour overrides on "${inst.name}"?\n\n` +
      `This will clear font, weight, size, colour, alignment, border, and ` +
      `image-filter settings for this instance and fall back to the global ` +
      `Site Styles. Content (text, links, images) is preserved.\n\n` +
      `This action cannot be undone.`
    )) return;
    try {
      const cleared = resetStyleConfig(inst.module_type, inst.config || {});
      await axios.put(
        `${API}/admin/module-instances/${inst.instance_id}`,
        { config: cleared },
        { withCredentials: true }
      );
      toast.success("Style overrides reset — instance now follows Site Styles");
      fetchInstances();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to reset styles");
    }
  };

  const filtered = instances
    .filter((i) => (filterType ? i.module_type === filterType : true))
    .filter((i) => (searchQuery.trim() ? (i.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase()) : true));

  return (
    <div className="space-y-6" data-testid="module-instances-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Module Instances</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create reusable module instances. Each instance can be placed on multiple pages.
          </p>
        </div>
        <Button
          data-testid="create-instance-btn"
          onClick={openCreate}
          className="bg-primary text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Instance
        </Button>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Label className="text-sm">Filter by type:</Label>
          <select
            data-testid="filter-instance-type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All types</option>
            {MODULE_TYPES.filter((t) => CONFIGURABLE_TYPES.includes(t.value)).sort((a, b) => a.label.localeCompare(b.label)).map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <Input
            data-testid="search-instance-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name…"
            className="h-9 w-56"
          />
          {(filterType || searchQuery) && (
            <button
              type="button"
              data-testid="clear-instance-filters"
              onClick={() => { setFilterType(""); setSearchQuery(""); }}
              className="text-xs text-muted-foreground hover:text-primary underline"
            >
              Clear
            </button>
          )}
          <span className="text-xs text-muted-foreground">{filtered.length} instance(s)</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
            <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No module instances yet. Create one to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Module Type</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="w-24">Enabled</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inst) => (
                <TableRow key={inst.instance_id} data-testid={`instance-row-${inst.instance_id}`}>
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      className="text-left hover:text-primary hover:underline focus:outline-none focus:text-primary"
                      onClick={() => setPreviewInstance(inst)}
                      data-testid={`preview-by-name-${inst.instance_id}`}
                      title="Preview this instance"
                    >
                      {inst.name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-primary"
                      onClick={() => setPreviewInstance(inst)}
                      data-testid={`preview-by-type-${inst.instance_id}`}
                      title="Preview this instance"
                    >
                      {getModuleTypeLabel(inst.module_type)}
                    </button>
                    {MODULE_TYPES.find((t) => t.value === inst.module_type)?.usesCookies && (
                      <span
                        className="inline-flex items-center gap-1 ml-1.5 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full align-middle"
                        title={MODULE_TYPES.find((t) => t.value === inst.module_type)?.cookieNote}
                        data-testid={`instance-uses-cookies-${inst.instance_id}`}
                      >
                        <Cookie className="h-3 w-3" /> Cookies
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {inst.is_default ? (
                      <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">Default</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      data-testid={`enable-instance-${inst.instance_id}`}
                      checked={inst.enabled !== false}
                      onCheckedChange={(checked) => toggleEnabled(inst, checked)}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {(inst.updated_at || "").split("T")[0]}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            data-testid={`row-actions-${inst.instance_id}`}
                            aria-label="Row actions"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem
                            onClick={() => openEdit(inst)}
                            data-testid={`edit-instance-${inst.instance_id}`}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDuplicate(inst)}
                            data-testid={`duplicate-instance-${inst.instance_id}`}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleResetStyles(inst)}
                            data-testid={`reset-styles-${inst.instance_id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reset Styles
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(inst)}
                            className="text-red-600 focus:text-red-700 focus:bg-red-50"
                            data-testid={`delete-instance-${inst.instance_id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create Module Instance</DialogTitle>
            <DialogDescription>
              Pick a module type and give your instance a descriptive name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Module Type</Label>
              <select
                data-testid="new-instance-type"
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {MODULE_TYPES.filter((t) => CONFIGURABLE_TYPES.includes(t.value)).sort((a, b) => a.label.localeCompare(b.label)).map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {MODULE_TYPES.find((t) => t.value === newType)?.description && (
                <p className="text-xs text-muted-foreground" data-testid="new-instance-type-description">
                  {MODULE_TYPES.find((t) => t.value === newType).description}
                </p>
              )}
              {MODULE_TYPES.find((t) => t.value === newType)?.usesCookies && (
                <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="new-instance-type-cookie-note">
                  <Cookie className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{MODULE_TYPES.find((t) => t.value === newType).cookieNote}</span>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Instance Name</Label>
              <Input
                data-testid="new-instance-name"
                placeholder="e.g., Homepage Hero, About Hero"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="confirm-create-instance">
              {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating…</> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeEditDialog(); }}>
        <DialogContent className="sm:max-w-[80vw] max-h-[90vh] overflow-y-auto" data-public-site>
          <DialogHeader>
            <DialogTitle>
              Edit Instance — {editing && getModuleTypeLabel(editing.module_type)}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between gap-3">
              <span>Configure all fields for this instance. Pages using it will reflect changes.</span>
              <span className="text-xs flex items-center gap-1.5 shrink-0" data-testid="autosave-indicator">
                {autosaveState === "saving" && (<><Loader2 className="h-3 w-3 animate-spin" /> Auto-saving…</>)}
                {autosaveState === "dirty" && (<><span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Unsaved</>)}
                {autosaveState === "saved" && (<><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" /> Saved</>)}
                {autosaveState === "error" && (<><span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Auto-save failed</>)}
              </span>
            </DialogDescription>
          </DialogHeader>
          {editing && MODULE_TYPES.find((t) => t.value === editing.module_type)?.usesCookies && (
            <p className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 -mt-1" data-testid="edit-instance-cookie-note">
              <Cookie className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{MODULE_TYPES.find((t) => t.value === editing.module_type).cookieNote}</span>
            </p>
          )}
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Instance Name</Label>
              <Input
                data-testid="edit-instance-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="pt-2 border-t space-y-4">
              {editing && (
                <ModuleConfigFields
                  moduleType={editing.module_type}
                  configFields={configFields}
                  setConfigFields={setConfigFields}
                  uploading={uploading}
                  onImageUpload={handleImageUpload}
                  allPages={allPages}
                  instanceId={editing.instance_id}
                />
              )}

              {/* Manage milestones — Master Admin CRUD on behalf of any
                  agent. Reuses the exact same CRUD UI agents see live,
                  scoped via agent_id. */}
              {editing?.module_type === "milestones" && (
                <div className="pt-4 border-t space-y-3" data-testid="edit-instance-milestones-admin">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Manage milestones (as agent)
                  </div>
                  <div className="max-w-sm">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Agent</Label>
                    <Select
                      value={selectedMilestoneAgentId}
                      onValueChange={setSelectedMilestoneAgentId}
                      disabled={milestoneAgentsLoading || milestoneAgents.length === 0}
                    >
                      <SelectTrigger className="mt-1" data-testid="milestones-admin-agent-select">
                        <SelectValue placeholder={milestoneAgentsLoading ? "Loading agents…" : "Choose an agent"} />
                      </SelectTrigger>
                      <SelectContent>
                        {milestoneAgents.map((a) => (
                          <SelectItem key={a.agent_id} value={a.agent_id} data-testid={`milestones-admin-agent-option-${a.agent_id}`}>
                            {a.name || a.email} ({a.properties_count} propert{a.properties_count === 1 ? "y" : "ies"})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!milestoneAgentsLoading && milestoneAgents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No agents found.</p>
                  ) : selectedMilestoneAgentId ? (
                    <div className="border-t border-border pt-4" data-public-site="true">
                      <MilestonesDashboardModule
                        config={configFields || {}}
                        adminAgentId={selectedMilestoneAgentId}
                        adminProperties={milestoneAgents.find((a) => a.agent_id === selectedMilestoneAgentId)?.linked_properties || []}
                      />
                    </div>
                  ) : null}
                </div>
              )}

              {/* Universal per-instance spacing + background controls —
                  apply to every module type. Modules sit flush by default;
                  admins can add breathing room and/or a section background
                  only where it's needed. */}
              <div className="pt-4 border-t">
                <div className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wide">Spacing</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Top gap (px)</label>
                    <input
                      type="number"
                      min="0"
                      max="256"
                      step="4"
                      value={configFields.top_gap ?? 10}
                      onChange={(e) => setConfigFields((prev) => ({ ...prev, top_gap: Number(e.target.value) || 0 }))}
                      placeholder="10"
                      className="w-full h-9 px-3 border border-input rounded text-sm"
                      data-testid="instance-config-top-gap"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Bottom gap (px)</label>
                    <input
                      type="number"
                      min="0"
                      max="256"
                      step="4"
                      value={configFields.bottom_gap ?? 10}
                      onChange={(e) => setConfigFields((prev) => ({ ...prev, bottom_gap: Number(e.target.value) || 0 }))}
                      placeholder="10"
                      className="w-full h-9 px-3 border border-input rounded text-sm"
                      data-testid="instance-config-bottom-gap"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Adds vertical padding above / below this module on the public page. Set to 0 for flush layout (default). Max 256px.
                </p>
              </div>

              <SectionBackgroundControls
                config={configFields}
                onChange={(patch) => setConfigFields((prev) => ({ ...prev, ...patch }))}
              />

              <CustomScriptControls
                config={configFields}
                onChange={(patch) => setConfigFields((prev) => ({ ...prev, ...patch }))}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview modal — opened by clicking the eye button, type badge,
          or instance name on any row. */}
      <InstancePreviewModal
        instance={previewInstance}
        open={!!previewInstance}
        onOpenChange={(o) => { if (!o) setPreviewInstance(null); }}
        onSaved={fetchInstances}
      />
    </div>
  );
}
